import { ROUTE_MATCHERS } from "@/constants/navigation"
import { ExpandMore } from "@mui/icons-material"
import { Box, Link } from "@mui/material"
import PropTypes from "prop-types"
import React, { useCallback } from "react"

/**
 * Renders a responsive navigation bar with active link highlighting and optional dropdown indicators.
 * Supports both desktop and mobile layouts, and handles special route matching for navigation state.
 *
 * @param {Object[]} navItems - Array of navigation items with path, label, and optional hasDropdown.
 * @param {boolean} [isMobile=false] - Whether to render the navigation in mobile layout.
 * @param {Function} [onNavClick] - Callback for navigation link clicks.
 * @returns {JSX.Element} Navigation bar component.
 */
const NavigationItems = React.memo(({ navItems, isMobile = false, onNavClick }) => {
  const currentPath = window.location.pathname

  const isLinkActive = useCallback(
    path => {
      if (currentPath === path) {
        return true
      }

      // Match special route patterns if they apply
      const hasSpecialMatch = Object.values(ROUTE_MATCHERS).some(
        matcher => matcher.test(path) && matcher.test(currentPath)
      )
      if (hasSpecialMatch) {
        return true
      }

      // Hierarchical matching
      return currentPath.startsWith(path + "/")
    },
    [currentPath]
  )

  return (
    <Box
      component="nav"
      data-testid="navigation-items"
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        width: isMobile ? "100%" : "auto",
        justifyContent: "space-between",
        py: isMobile ? 0 : 0,
      }}
    >
      {navItems.map(({ path, label, hasDropdown }) => {
        const isActive = isLinkActive(path)
        return (
          <Link
            key={path}
            href={path}
            onClick={onNavClick}
            data-turbolinks="false"
            aria-current={isActive ? "page" : undefined}
            underline="none"
            data-testid={`navigation-item-${path}`}
            sx={{
              color: "text.primary",
              fontWeight: isActive ? 600 : 400,
              textTransform: "none",
              px: isMobile ? 3 : 1.5,
              py: isMobile ? 2 : 0.5,
              display: "flex",
              alignItems: "center",
              width: "100%",
              position: "relative",
              whiteSpace: "nowrap",
              height: isMobile ? "auto" : "2rem",
              mx: isMobile ? 0 : 0.5,
              borderRadius: isMobile ? 0 : 1,
              transition: "all 0.2s ease",
              "&:after":
                isActive && isMobile
                  ? {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      width: "4px",
                      height: "100%",
                      bgcolor: "primary.main",
                    }
                  : isActive
                  ? {
                      content: '""',
                      position: "absolute",
                      bottom: -1,
                      left: "10%",
                      width: "80%",
                      height: "2px",
                      bgcolor: "primary.main",
                    }
                  : {},
              "&:hover": {
                color: "primary.main",
                textDecoration: "none",
                bgcolor: isMobile ? "action.hover" : "transparent",
              },
            }}
          >
            {label}
            {hasDropdown && <ExpandMore sx={{ ml: 0.5, fontSize: 20 }} />}
          </Link>
        )
      })}
    </Box>
  )
})

NavigationItems.propTypes = {
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      hasDropdown: PropTypes.bool,
    })
  ).isRequired,
  isMobile: PropTypes.bool,
  onNavClick: PropTypes.func,
}

NavigationItems.displayName = "NavigationItems"

export default NavigationItems
